"use client";

/**
 * Why a send is waiting, said beside the send key. Moved out of MessageInput
 * verbatim (same spans, same testids) so the Dock's pill says the same two
 * things on a task's screen.
 */
import { useConnectionStore } from "@/stores/connectionStore";
import { useConnectionHealthStore } from "@/stores/connectionHealthStore";

/**
 * Deploy pause: an update window with the socket down pauses sends honestly
 * (the draft stays in the field, and un-pauses itself on the successor
 * connect). While the socket is still up mid-turn, sends keep working.
 * Offline: the health rail says down and it is not a deploy window. The
 * composer stays usable; this only says why.
 */
export function useSendPause(): { deployPaused: boolean; offline: boolean } {
  const deployPaused = useConnectionStore(
    (s) => !!s.deployUpdating && !s.isSocketConnected
  );
  const offline =
    useConnectionHealthStore((s) => s.health === "down") && !deployPaused;
  return { deployPaused, offline };
}

export function ConnectionHints({
  deployPaused,
  offline,
}: {
  deployPaused: boolean;
  offline: boolean;
}) {
  return (
    <>
      {deployPaused && (
        <span
          className="flex items-center gap-1.5 text-3 text-gold-ink"
          data-testid="deploy-paused-hint"
        >
          <span
            className="w-2 h-2 rounded-full shrink-0 bg-gold-molten-radial animate-deploy-capsule-breathe motion-reduce:animate-none w-[6px] h-[6px]"
            aria-hidden
          />
          Sends after the update
        </span>
      )}
      {offline && (
        <span
          className="flex items-center gap-1.5 text-3 text-fg-muted"
          data-testid="offline-hint"
        >
          <span
            className="rounded-full w-[6px] h-[6px] bg-palette-gray"
            aria-hidden
          />
          Offline
        </span>
      )}
    </>
  );
}
